/**
 * The Design studio: the content-design half of the newsletter workspace, now a
 * two-pane LIVE STUDIO. The left rail carries the curation tools (a collapsible
 * "Sources" disclosure over the existing SectionsPanel, a read-only rail of the
 * issue's content blocks, and the docked refine chat); the right pane is the
 * IssueRenderer, a reader-accurate render of the PERSISTED draft issue.
 *
 * The draft issue auto-loads on mount (GET /issue, which the backend lazily
 * assembles + persists on first read, so the studio "opens already drafted") and
 * auto-regenerates (POST /issue/generate, debounced) whenever the selected sections
 * change. Its cache is hoisted to the workspace and guarded by a built-for-key, so
 * a billed load/generate never re-fires merely because the owner returned to Design.
 *
 * The left rail is now EDITABLE (P1.5): each block can be renamed, rewritten,
 * reordered, and kept or dismissed, applied optimistically to the in-memory issue
 * (so the right pane re-renders at once) and persisted with a debounced last-write-
 * wins PATCH. Two side-by-side panes are a desktop layout (>=1024px); below that
 * the single column collapses to a Curate|Preview toggle.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Notification,
  SegmentedControl,
  Skeleton,
  type SegmentedControlOption,
} from '@nuff/ui/react';

import {
  ApiError,
  generateIssue,
  getIssue,
  patchIssue,
  refreshOpening,
  regenerateBlock,
  seedInstructions,
  setSectionInstruction,
  setSectionTreatment,
  type ContentBlock,
  type Issue,
  type MissedLikedNewsletter,
  type SeededInstruction,
  type Treatment,
} from '../../api';
import { type BlockFocusTarget } from './BlockCard';
import {
  moveBlock,
  railBlocksInOrder,
  setBlockBody,
  setBlockTitle,
  toIssueEditInput,
  toggleBlockStatus,
} from './blockEdits';
import { Inspector, type SeedRollupEntry, type SeedUx } from './Inspector';
import { IssueRenderer, issueSectionSignature, sectionSetKey } from './IssueRenderer';
import { RefineChat, SectionsPanel } from './panels';
import { PipelineMap } from './PipelineMap';
import {
  derivePipelineSections,
  derivePipelineSources,
  type SelectedNode,
  sourceColorMap,
} from './pipeline';
import { WireGraph } from './WireGraph';
import { useNewsletterWorkspace } from './workspace';

/**
 * Which single region the responsive (<1024px) toggle shows; on desktop both regions
 * (Map · Inspector) show side by side. The value drives the CSS via data-pane. The
 * rendered issue now lives ONLY in Preview mode, so Write is a two-region layout and
 * the toggle no longer offers an Issue pane; the studio opens on the Inspector so a
 * small screen lands on the editing controls for the auto-selected lead section.
 */
type StudioPane = 'map' | 'inspector';

const PANE_OPTIONS: readonly SegmentedControlOption<StudioPane>[] = [
  { value: 'map', label: 'Map' },
  { value: 'inspector', label: 'Inspector' },
];

/**
 * The studio's top-level VIEW mode. Write is the default three-region compose surface
 * (map · live issue · inspector). Wire is an ALTERNATIVE, never a replacement: the full
 * drawn-edge pipeline graph beside the same inspector, for tracing provenance. Preview
 * is the issue on its own, full-width and reader-accurate, with none of the editing
 * chrome - "what your readers will see". Write + Wire share one selection model, so
 * switching between them keeps the inspector on the same node.
 */
type StudioMode = 'write' | 'wire' | 'preview';

const MODE_OPTIONS: readonly SegmentedControlOption<StudioMode>[] = [
  { value: 'write', label: 'Write' },
  { value: 'wire', label: 'Wire' },
  { value: 'preview', label: 'Preview' },
];

/** How long to wait after a curation change before auto-regenerating the issue. */
const ISSUE_REGEN_DEBOUNCE_MS = 500;

/**
 * How often to poll GET /issue while the backend reports `generating`. Generation
 * now runs off the request path (the generator Lambda writes full prose on every
 * section without the API's ~29s ceiling), so the studio polls for the finished
 * issue rather than blocking on one long request. Mirrors the sending-identity poll:
 * an in-flight latch keeps it polite, and the effect stops the moment the status
 * leaves `generating` or the studio unmounts.
 */
const ISSUE_POLL_INTERVAL_MS = 5000;

/**
 * What Regenerate does, kept off the top of the pane (so the email leads) and shown
 * as the Regenerate buttons' tooltip instead. Shared so both buttons read identically.
 */
const REGEN_HINT =
  'Regenerate rebuilds this draft from your current sources with our AI and replaces edits you have made here. It takes a few seconds.';

/** True when the viewer asked the OS to minimise motion, so we skip smooth scroll/FLIP. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Scroll an element into view, honouring reduced-motion (no smooth animation). */
function revealElement(el: Element | null): void {
  if (el === null) return;
  el.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

/**
 * Whether an element sits fully outside the viewport (above the top or below the
 * bottom). The reader pane is `position: sticky`, not its own scroll container, so
 * `scrollIntoView` scrolls the whole PAGE; we only want that when the target is
 * genuinely off-screen, so a rail hover over an already-visible block never nudges
 * the page.
 */
function isOutOfView(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  return rect.bottom <= 0 || rect.top >= viewportHeight;
}

/**
 * How long to coalesce block edits before a single PATCH. Longer than a fast
 * typist's inter-keystroke gap so a rewrite is ONE save, not one per keystroke.
 */
const ISSUE_PATCH_DEBOUNCE_MS = 600;

export function DesignStudio(): React.JSX.Element {
  const {
    deps,
    newsletter,
    setNewsletter,
    sections,
    setSections,
    discoverState,
    discoverError,
    runDiscover,
    issueCache,
    setIssueCache,
    refineTurns,
    setRefineTurns,
    refineInput,
    setRefineInput,
    refineNextId,
  } = useNewsletterWorkspace();

  // Navigate the owner to Operations (About this newsletter) from the seed empty-state.
  const navigate = useNavigate();

  const hasTopics = newsletter.topics.length > 0;
  // The staleness axis is SET-based (which sections are selected), never their
  // order: reordering selected sections must not auto-regenerate and discard manual
  // block edits (#1/#4). It is compared against issueSectionSignature, which is
  // likewise a set over the draft's section blocks.
  const selectionKey = sectionSetKey(
    sections.filter((section) => section.status === 'selected').map((section) => section.sectionId),
  );
  const hasSelected = selectionKey.length > 0;
  const sectionsLoading = discoverState === 'loading' || discoverState === 'discovering';
  // There is content to assemble once the owner has kept sections, sections exist,
  // or there are topics to write an intro from; the backend 400s otherwise.
  const canAssemble = hasSelected || sections.length > 0 || hasTopics;

  const newsletterId = newsletter.newsletterId;
  const { state: issueState, issue, error: issueError, builtForKey, loaded } = issueCache;

  // Default the responsive (single-column) view to the INSPECTOR region, so a phone
  // owner opens onto the editing controls for the auto-selected lead section. The
  // rendered issue lives in Preview mode now, not in Write, so there is no Issue pane
  // here. Above 1024px both regions show side by side, so this only affects the toggle.
  const [mobilePane, setMobilePane] = useState<StudioPane>('inspector');

  // The top-level view mode: Write (default, the three-region compose surface) or Wire
  // (the drawn-edge pipeline graph beside the same inspector). Both drive the SAME
  // selectedNode, so toggling modes never loses the inspector's place.
  const [mode, setMode] = useState<StudioMode>('write');

  // The block currently highlighted across the regions (selecting/hovering a section
  // node or reader block lights the matching block), a cheap link that makes the three
  // regions feel like one design rather than separate copies of the same content.
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);

  // The pipeline node the inspector is bound to (a source, a section, or the issue),
  // or null to fall back to the Issue shape. Selecting a SECTION node also drives
  // activeBlockId (a section node's id IS its content block id), so the map, the
  // centre issue, and the inspector stay in lockstep.
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  // The current selection key, mirrored into a ref so the one-time auto-load effect
  // can read it WITHOUT taking it as a dependency (which would re-run the load).
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;

  // The selection key we have already scheduled/run a (re)generate for in THIS mount.
  // Seeded from the persisted built-key so a remount never re-schedules a build that
  // already ran.
  const scheduledKey = useRef<string | null>(builtForKey);

  // ---- Async generation polling ----
  // Generation runs off the request path, so a generate/first-open returns a
  // `generating` issue and the studio POLLS GET /issue until it flips to a finished
  // one. `pollInFlight` is the in-flight latch (an interval tick never overlaps a
  // previous, unresolved one); `pendingBuiltKey` is the selection key to stamp as the
  // built-for-key once the in-flight generation resolves (null = derive it from the
  // finished issue's own section signature).
  const pollInFlight = useRef(false);
  const pendingBuiltKey = useRef<string | null>(null);

  // ---- Block-editing machinery (optimistic edit -> debounced PATCH -> rollback) ----
  // The optimistic issue is the SOURCE OF TRUTH: an edit mutates it in memory (so the
  // right pane re-renders synchronously), and a debounced PATCH persists the FULL
  // block array (last-write-wins). `issueRef` mirrors the current optimistic issue so
  // the debounce can read the latest without re-arming on every keystroke; `baseline`
  // is the last SERVER-persisted issue, the target a failed PATCH rolls back to.
  // Seeded from the cache so both survive a Design/Operations tab round trip.
  const issueRef = useRef<Issue | null>(issue);
  const baselineRef = useRef<Issue | null>(issue);
  const patchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const patchInFlight = useRef(false);
  const patchDirty = useRef(false);
  // Bumped whenever a load/regenerate takes OWNERSHIP of the issue. A PATCH that was
  // in flight across such an event compares the epoch it captured and DISCARDS its
  // result, so a stale manual edit can neither clobber nor be clobbered by a
  // regenerate (which deliberately rebuilds from sources and drops manual edits).
  const issueEpoch = useRef(0);
  // Per-section AI-instruction editing: optimistic (mirrored into `sections` so the
  // controlled textarea stays responsive), then a debounced save keyed by sectionId.
  // A SEPARATE timer + pending map from the block-edit debounce, so an instruction save
  // and a block PATCH never cancel each other.
  const instructionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInstructions = useRef<Map<string, string>>(new Map());
  // Mirrors the latest flushInstructions so the empty-deps unmount effect can flush a
  // pending instruction save without re-arming on every render (like flushRef).
  const flushInstructionsRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Per-section TREATMENT editing (the honest "how much AI" knob): SAME optimistic +
  // debounced-save shape as the instruction lever, on its OWN timer + pending map so a
  // treatment pick and an instruction/block save never cancel each other. Debounced (not
  // fired per pick) because the select's arrow keys can step through options quickly.
  const treatmentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTreatments = useRef<Map<string, Treatment>>(new Map());
  const flushTreatmentsRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const [editError, setEditError] = useState<string | null>(null);
  // Per-section regenerate feedback. `regeneratingBlockId` is the block whose sync regen
  // is in flight (dims that block, disables the whole-issue Regenerate, and shows the
  // button's busy state); `regenStatus` is the aria-live outcome shown next to the
  // button (updated / no-op / error); `justUpdatedBlockId` gives the swapped block a
  // brief "updated" wash. `openingMaybeStale` prompts an on-demand subject+intro resync
  // after a section changed under the opening; `refreshingOpening` latches that call.
  const [regeneratingBlockId, setRegeneratingBlockId] = useState<string | null>(null);
  const [regenStatus, setRegenStatus] = useState<{
    readonly blockId: string;
    readonly kind: 'updated' | 'noop' | 'error';
    readonly message: string;
  } | null>(null);
  const [justUpdatedBlockId, setJustUpdatedBlockId] = useState<string | null>(null);
  const [openingMaybeStale, setOpeningMaybeStale] = useState(false);
  const [refreshingOpening, setRefreshingOpening] = useState(false);
  // ---- E2.1 opt-in seed-from-liked-newsletters (WHOLE-ISSUE) ----
  // The owner triggers this; we NEVER auto-run it, so it can never silently change the
  // E2 baseline. `seedState` gates the one-in-flight button; `seedDrafts` are the still-
  // undecided per-section drafts awaiting review (keyed by sectionId), which back the
  // persistent roll-up and the per-section card; `seedMissed`/`seedRanEmpty` back the
  // honest status lines; `seedKeptSectionId` confirms the apply step after a keep;
  // `seedError` surfaces a failed run. `seedDecided` remembers every section the owner has
  // kept or dismissed so a re-run is additive-only and never resurrects a dismissed draft.
  const [seedState, setSeedState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [seedDrafts, setSeedDrafts] = useState<ReadonlyMap<string, SeededInstruction>>(new Map());
  const [seedMissed, setSeedMissed] = useState<readonly MissedLikedNewsletter[]>([]);
  const [seedRanEmpty, setSeedRanEmpty] = useState(false);
  const [seedKeptSectionId, setSeedKeptSectionId] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const seedDecided = useRef<Set<string>>(new Set());
  // "Dirty" = the owner has made manual block edits since the last load/generate, so
  // the draft diverges from a pure section assembly. When the selected sections then
  // change, we must NOT silently regenerate over those edits: we prompt instead (#4).
  const [dirty, setDirty] = useState(false);
  const [sourcesChanged, setSourcesChanged] = useState(false);
  // After a dismiss/keep or reorder we move keyboard focus to the equivalent control
  // on the affected card, so a keyboard user is not dropped to the top of the page
  // (#6). Cleared by the card once it has taken focus.
  const [focusTarget, setFocusTarget] = useState<BlockFocusTarget | null>(null);
  const clearFocusTarget = useCallback((): void => setFocusTarget(null), []);

  // Hovering/focusing a RAIL card highlights the matching reader block AND scrolls
  // it into view, so the owner sees exactly where a block lands in the email.
  const onRailHoverChange = useCallback((id: string | null): void => {
    setActiveBlockId(id);
    if (id !== null) {
      // Only scroll when the block is actually off-screen: if it is already visible,
      // scrolling the (sticky, non-scroll-container) page would jump it needlessly.
      const el = document.getElementById(`issue-block-${id}`);
      if (el !== null && isOutOfView(el)) {
        revealElement(el);
      }
    }
  }, []);
  // Selecting any pipeline node binds the inspector; a SECTION node additionally
  // cross-highlights its rendered block in the centre issue (and reveals it if it has
  // scrolled off-screen), reusing the same highlight plumbing as the hover link.
  const onSelectNode = useCallback((node: SelectedNode): void => {
    setSelectedNode(node);
    if (node.kind === 'section') {
      setActiveBlockId(node.id);
      const el = document.getElementById(`issue-block-${node.id}`);
      if (el !== null && isOutOfView(el)) {
        revealElement(el);
      }
    } else {
      setActiveBlockId(null);
    }
  }, []);

  // A stable scheduler that (re)arms the debounce. `flushRef` breaks the
  // scheduler<->flush cycle so both can stay referentially stable.
  const flushRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const schedulePatch = useCallback((): void => {
    if (patchTimer.current !== null) {
      clearTimeout(patchTimer.current);
    }
    patchTimer.current = setTimeout(() => {
      patchTimer.current = null;
      void flushRef.current();
    }, ISSUE_PATCH_DEBOUNCE_MS);
  }, []);

  // Take ownership of the issue on a load/regenerate: invalidate any in-flight or
  // pending PATCH so a stale manual edit cannot land on top of the fresh issue.
  const takeIssueOwnership = useCallback((): void => {
    issueEpoch.current += 1;
    patchDirty.current = false;
    // A fresh assembly owns the issue: the prior manual edits are gone, so the draft
    // is clean again and the "sources changed" prompt (if any) is resolved.
    setDirty(false);
    setSourcesChanged(false);
    // A full regenerate rebuilds the spine (subject + intro) from scratch, so any
    // "opening may be stale" prompt from a per-section regen is resolved too.
    setOpeningMaybeStale(false);
    setRegenStatus(null);
    // The fresh assembly mints new block ids, so any node selected against the OLD
    // blocks is now stale. Clear it; the auto-select effect re-picks the lead once the
    // rebuilt issue settles, so the inspector never dangles over gone blocks.
    setSelectedNode(null);
    setActiveBlockId(null);
    if (patchTimer.current !== null) {
      clearTimeout(patchTimer.current);
      patchTimer.current = null;
    }
  }, []);

  // Settle a freshly fetched/generated issue into the cache. A `generating` issue
  // means the backend is still building it off the request path, so we keep any prior
  // issue on screen, remember the key to stamp on completion, and leave the polling
  // effect to keep checking. A finished issue takes ownership of the screen and stamps
  // the built-for-key (the caller's intended key, else derived from the issue's own
  // section signature vs the current selection, so an up-to-date draft never re-bills
  // and a stale one rebuilds exactly once).
  const settleIssue = useCallback(
    (fresh: Issue, intendedKey: string | null): void => {
      if (fresh.status === 'generating') {
        pendingBuiltKey.current = intendedKey;
        setIssueCache((prev) => ({ ...prev, state: 'generating', error: null, loaded: true }));
        return;
      }
      if (fresh.status === 'error') {
        // A TERMINAL generation stop (today: the monthly AI budget was reached). Stop
        // polling and show the calm reason the server persisted, rather than spinning on
        // `generating` forever or treating an empty shell as a finished issue.
        pendingBuiltKey.current = null;
        setIssueCache((prev) => ({
          ...prev,
          state: 'error',
          error:
            fresh.statusReason ??
            "Our AI is paused for this month because we reached this month's usage limit. It resumes next month.",
          loaded: true,
        }));
        return;
      }
      const derived = issueSectionSignature(fresh);
      const builtKey =
        intendedKey ?? (derived === selectionKeyRef.current ? selectionKeyRef.current : derived);
      scheduledKey.current = builtKey;
      baselineRef.current = fresh;
      issueRef.current = fresh;
      // A finished issue (load, poll, or full regenerate settle) carries a fresh spine,
      // so an "opening may be stale" prompt from a prior per-section regen no longer holds.
      setOpeningMaybeStale(false);
      setIssueCache((prev) => ({
        ...prev,
        state: 'ready',
        issue: fresh,
        error: null,
        loaded: true,
        builtForKey: builtKey,
      }));
    },
    [setIssueCache],
  );

  const flushPatch = useCallback(async (): Promise<void> => {
    // A PATCH is already flying: its completion re-checks the dirty flag and re-arms,
    // so patches stay strictly serial (no last-write-wins race between two patches).
    if (patchInFlight.current) {
      return;
    }
    const snapshot = issueRef.current;
    const baseline = baselineRef.current;
    if (snapshot === null || !patchDirty.current) {
      return;
    }
    patchInFlight.current = true;
    patchDirty.current = false;
    const epochAtSend = issueEpoch.current;
    // C1 suspenders: capture the newsletter this PATCH is for, so a result that
    // resolves after a newsletter switch that raced the unmount can never land on a
    // different newsletter's draft (the keyed remount handles the common case; this
    // guards the in-flight-across-switch race).
    const nid = newsletterId;
    try {
      const saved = await patchIssue(deps, nid, toIssueEditInput(snapshot));
      // A regenerate/load took ownership mid-flight, or the newsletter changed under
      // us: the fresh issue wins, so drop this result silently rather than resurrect
      // the edits it replaced (or write them onto another newsletter).
      if (issueEpoch.current === epochAtSend && newsletterId === nid) {
        baselineRef.current = saved;
        setEditError(null);
      }
    } catch (caught) {
      // A 409 is the benign optimistic-concurrency conflict: the draft this patch
      // targeted was replaced by a regenerate server-side. The client already
      // discarded it (its epoch guard), so we neither roll back nor surface an error
      // for the expected conflict; the fresh issue already owns the screen.
      const isConflict = caught instanceof ApiError && caught.status === 409;
      if (issueEpoch.current === epochAtSend && newsletterId === nid && !isConflict) {
        // Roll the optimistic edits back to the last persisted issue and surface the
        // error, so the state is never left stuck on edits that did not save.
        patchDirty.current = false;
        issueRef.current = baseline;
        setIssueCache((prev) => ({ ...prev, issue: baseline }));
        setEditError(caught instanceof Error ? caught.message : 'Could not save your edits.');
      }
    } finally {
      patchInFlight.current = false;
      // Edits that arrived DURING the flight (and were not invalidated) still need
      // saving: re-arm once so they are not lost.
      if (patchDirty.current && issueEpoch.current === epochAtSend && newsletterId === nid) {
        schedulePatch();
      }
    }
  }, [deps, newsletterId, schedulePatch, setIssueCache]);
  flushRef.current = flushPatch;

  // Apply one optimistic edit: mutate the in-memory issue (right pane re-renders at
  // once), keep `issueRef` in sync for the debounce, and schedule a PATCH. Never
  // touches the sections, so a manual edit can never trigger an auto-regenerate.
  const applyEdit = useCallback(
    (mutate: (issue: Issue) => Issue): void => {
      setIssueCache((prev) => {
        if (prev.issue === null) return prev;
        const nextIssue = mutate(prev.issue);
        issueRef.current = nextIssue;
        return { ...prev, issue: nextIssue };
      });
      patchDirty.current = true;
      // A manual edit makes the draft diverge from a section assembly, so a later
      // section change must ask before rebuilding rather than silently overwrite.
      setDirty(true);
      schedulePatch();
    },
    [schedulePatch, setIssueCache],
  );

  const onToggleKept = useCallback(
    (block: ContentBlock): void => {
      setFocusTarget({ id: block.id, kind: 'toggle' });
      applyEdit((iss) => toggleBlockStatus(iss, block.id));
    },
    [applyEdit],
  );
  const onMoveBlock = useCallback(
    (block: ContentBlock, direction: -1 | 1): void => {
      setFocusTarget({ id: block.id, kind: direction === -1 ? 'up' : 'down' });
      applyEdit((iss) => moveBlock(iss, block.id, direction));
    },
    [applyEdit],
  );
  const onEditTitle = useCallback(
    (id: string, title: string): void => applyEdit((iss) => setBlockTitle(iss, id, title)),
    [applyEdit],
  );
  const onEditBody = useCallback(
    (id: string, body: string): void => applyEdit((iss) => setBlockBody(iss, id, body)),
    [applyEdit],
  );

  // Persist any pending debounced instruction edits (keyed by sectionId, last-write-wins
  // per section). A save failure surfaces via the edit-error channel; the optimistic
  // section state already reflects the text, so the textarea never fights the caret.
  const flushInstructions = useCallback(async (): Promise<void> => {
    const pending = pendingInstructions.current;
    if (pending.size === 0) {
      return;
    }
    const entries = [...pending.entries()];
    pending.clear();
    await Promise.all(
      entries.map(async ([sectionId, text]) => {
        try {
          await setSectionInstruction(deps, newsletterId, sectionId, text);
          setEditError(null);
        } catch (caught) {
          setEditError(
            caught instanceof Error ? caught.message : 'Could not save that instruction.',
          );
        }
      }),
    );
  }, [deps, newsletterId]);
  flushInstructionsRef.current = flushInstructions;

  // Edit one section's AI instruction: mirror it into `sections` at once (the textarea
  // reads from there) and schedule a debounced save. Never touches the issue blocks, so
  // it can never trigger an auto-regenerate.
  const onEditInstruction = useCallback(
    (sectionId: string, text: string): void => {
      setSections(
        sections.map((section) =>
          section.sectionId === sectionId ? { ...section, instruction: text } : section,
        ),
      );
      pendingInstructions.current.set(sectionId, text);
      if (instructionTimer.current !== null) {
        clearTimeout(instructionTimer.current);
      }
      instructionTimer.current = setTimeout(() => {
        instructionTimer.current = null;
        void flushInstructions();
      }, ISSUE_PATCH_DEBOUNCE_MS);
    },
    [sections, setSections, flushInstructions],
  );

  // Persist any pending debounced treatment picks (keyed by sectionId, last-write-wins per
  // section). Mirrors flushInstructions: a save failure surfaces via the edit-error channel,
  // and the optimistic section state already reflects the pick so the select never jumps back.
  const flushTreatments = useCallback(async (): Promise<void> => {
    const pending = pendingTreatments.current;
    if (pending.size === 0) {
      return;
    }
    const entries = [...pending.entries()];
    pending.clear();
    await Promise.all(
      entries.map(async ([sectionId, treatment]) => {
        try {
          await setSectionTreatment(deps, newsletterId, sectionId, treatment);
          setEditError(null);
        } catch (caught) {
          setEditError(caught instanceof Error ? caught.message : 'Could not save that treatment.');
        }
      }),
    );
  }, [deps, newsletterId]);
  flushTreatmentsRef.current = flushTreatments;

  // Edit one section's TREATMENT: mirror it into `sections` at once (the select reads from
  // there) and schedule a debounced save. Never touches the issue blocks, so it can never
  // trigger an auto-regenerate; the change lands on the next per-section (or full) regenerate.
  const onEditTreatment = useCallback(
    (sectionId: string, treatment: Treatment): void => {
      setSections(
        sections.map((section) =>
          section.sectionId === sectionId ? { ...section, treatment } : section,
        ),
      );
      pendingTreatments.current.set(sectionId, treatment);
      if (treatmentTimer.current !== null) {
        clearTimeout(treatmentTimer.current);
      }
      treatmentTimer.current = setTimeout(() => {
        treatmentTimer.current = null;
        void flushTreatments();
      }, ISSUE_PATCH_DEBOUNCE_MS);
    },
    [sections, setSections, flushTreatments],
  );

  // E2.1: opt-in, WHOLE-ISSUE. Ask our AI to DRAFT a starter instruction for every written
  // section from the newsletters the owner loves. Nothing persists here: the drafts are
  // returned for review (keep / dismiss), so the E2 "no instruction = unchanged" baseline
  // holds until the owner keeps one. A re-run is additive-only: sections the owner already
  // kept or dismissed (tracked in `seedDecided`) are NEVER resurrected (#6). Disabled while
  // in flight (one user-triggered burst).
  const onSeedInstructions = useCallback(async (): Promise<void> => {
    setSeedError(null);
    setSeedKeptSectionId(null);
    setSeedState('loading');
    try {
      const result = await seedInstructions(deps, newsletterId);
      const fresh = result.seeds.filter((seed) => !seedDecided.current.has(seed.sectionId));
      setSeedDrafts(new Map(fresh.map((seed) => [seed.sectionId, seed])));
      setSeedMissed(result.missed);
      // "Ran empty" reflects the run producing no usable starters at all (nothing readable),
      // which drives the single honest failure line rather than a stale roll-up.
      setSeedRanEmpty(result.seeds.length === 0);
      setSeedState('done');
    } catch (caught) {
      setSeedError(
        caught instanceof Error ? caught.message : 'Could not draft from the newsletters you love.',
      );
      setSeedState('idle');
    }
  }, [deps, newsletterId]);

  // Keep one draft: fill the editable instruction (which persists via the debounced save),
  // record the decision (so a re-run never resurrects it), drop it from the review set, and
  // confirm the apply step so the owner knows to Regenerate to see it (#4).
  const onKeepSeed = useCallback(
    (seed: SeededInstruction): void => {
      onEditInstruction(seed.sectionId, seed.instruction);
      seedDecided.current.add(seed.sectionId);
      setSeedKeptSectionId(seed.sectionId);
      setSeedDrafts((prev) => {
        const next = new Map(prev);
        next.delete(seed.sectionId);
        return next;
      });
    },
    [onEditInstruction],
  );

  // Dismiss one draft without using it: record the decision (so a re-run never resurrects
  // it) and drop it from the review set. The section keeps whatever instruction it had.
  const onDismissSeed = useCallback((sectionId: string): void => {
    seedDecided.current.add(sectionId);
    setSeedKeptSectionId((prev) => (prev === sectionId ? null : prev));
    setSeedDrafts((prev) => {
      const next = new Map(prev);
      next.delete(sectionId);
      return next;
    });
  }, []);

  // Route to Basics, under About this newsletter, where liked newsletters are edited.
  const onGoToAbout = useCallback((): void => void navigate('basics'), [navigate]);

  // Regenerate ONE section's block synchronously from fresh material + its instruction.
  // The backend returns a full issue with the SAME block ids + issueId, so we settle it
  // like a successful PATCH (adopt it as the new baseline) rather than a full regenerate:
  // we bump the epoch to drop any in-flight block PATCH, but do NOT clear the selection,
  // so the inspector stays put on this section. The current (optimistic) instruction is
  // passed so the backend saves + uses the just-typed text, dodging the save-vs-regen race.
  const onRegenerateSection = useCallback(
    async (block: ContentBlock): Promise<void> => {
      const section = sections.find((candidate) => candidate.sectionId === block.sectionId);
      const priorBody = issueRef.current?.blocks.find((b) => b.id === block.id)?.body ?? block.body;
      // LOST-UPDATE mitigation: flush any pending debounced block PATCH first, so the
      // server draft already carries the owner's other-block edits before the regen
      // reads it. (Residual RMW window remains: two body-only writers race and
      // expectedIssueId does not guard concurrent body-only edits, see E2 fast-follow.)
      if (patchTimer.current !== null) {
        clearTimeout(patchTimer.current);
        patchTimer.current = null;
      }
      await flushRef.current();
      setRegenStatus(null);
      setEditError(null);
      setRegeneratingBlockId(block.id);
      // Capture the epoch BEFORE the await: if a full regenerate takes ownership while
      // this is in flight, we must DISCARD the result (never clobber the fresh issue nor
      // stop its poll), mirroring flushPatch.
      const epochAtSend = issueEpoch.current;
      try {
        const fresh = await regenerateBlock(deps, newsletterId, block.id, section?.instruction);
        if (issueEpoch.current !== epochAtSend) {
          // A full regenerate owns the issue now: drop this result silently.
          return;
        }
        const freshBody = fresh.blocks.find((b) => b.id === block.id)?.body ?? '';
        if (freshBody === priorBody) {
          // SOFT MISS: no fresher material, the body is unchanged. Say so plainly rather
          // than silently swapping nothing (and do not adopt, do not mark opening stale).
          setRegenStatus({
            blockId: block.id,
            kind: 'noop',
            message: 'No fresher material right now, the text is unchanged.',
          });
          return;
        }
        // Adopt the fresh issue as the new baseline + optimistic source; bump the epoch to
        // drop any in-flight PATCH. Selection is untouched (same block id), so the
        // inspector stays put on this section.
        issueEpoch.current += 1;
        patchDirty.current = false;
        if (patchTimer.current !== null) {
          clearTimeout(patchTimer.current);
          patchTimer.current = null;
        }
        baselineRef.current = fresh;
        issueRef.current = fresh;
        setIssueCache((prev) => ({
          ...prev,
          state: 'ready',
          issue: fresh,
          error: null,
          loaded: true,
        }));
        // The subject + intro (which preview THESE stories) may no longer match: prompt an
        // on-demand opening refresh, and confirm the swap with a brief "updated" wash.
        setOpeningMaybeStale(true);
        setJustUpdatedBlockId(block.id);
        setRegenStatus({
          blockId: block.id,
          kind: 'updated',
          message: `${section?.title ?? 'Section'} updated just now`,
        });
      } catch (caught) {
        if (issueEpoch.current !== epochAtSend) {
          return;
        }
        setRegenStatus({
          blockId: block.id,
          kind: 'error',
          message: caught instanceof Error ? caught.message : 'Could not regenerate this section.',
        });
      } finally {
        setRegeneratingBlockId((current) => (current === block.id ? null : current));
      }
    },
    [deps, newsletterId, sections, setIssueCache],
  );

  // Refresh the issue's opening (subject + intro) on demand after a per-section regen
  // left it stale. Settled in place, epoch-guarded exactly like onRegenerateSection: a
  // full regenerate that took ownership mid-flight discards this result.
  const onRefreshOpening = useCallback(async (): Promise<void> => {
    setRefreshingOpening(true);
    setEditError(null);
    const epochAtSend = issueEpoch.current;
    try {
      const fresh = await refreshOpening(deps, newsletterId);
      if (issueEpoch.current !== epochAtSend) {
        return;
      }
      issueEpoch.current += 1;
      patchDirty.current = false;
      if (patchTimer.current !== null) {
        clearTimeout(patchTimer.current);
        patchTimer.current = null;
      }
      baselineRef.current = fresh;
      issueRef.current = fresh;
      setIssueCache((prev) => ({
        ...prev,
        state: 'ready',
        issue: fresh,
        error: null,
        loaded: true,
      }));
      setOpeningMaybeStale(false);
    } catch (caught) {
      if (issueEpoch.current !== epochAtSend) {
        return;
      }
      setEditError(caught instanceof Error ? caught.message : 'Could not refresh the opening.');
    } finally {
      setRefreshingOpening(false);
    }
  }, [deps, newsletterId, setIssueCache]);

  // Keep the edit refs current when the issue changes for reasons OTHER than a local
  // edit (a fresh load/regenerate), so a subsequent edit builds on the right base. When
  // the change is NOT a pending optimistic edit (patchDirty false), the new issue IS the
  // server-persisted one, so it also seeds `baselineRef` (the rollback target). This is
  // what adopts the issue the WORKSPACE loaded on first open (the Studio no longer runs
  // its own initial load), so a later failed PATCH rolls back to the real draft, never null.
  useEffect(() => {
    issueRef.current = issue;
    if (!patchDirty.current) {
      baselineRef.current = issue;
    }
  }, [issue]);

  // Regenerate the persisted draft from the current sections (POST /issue/generate).
  // When `key` is given it is recorded as the built-for-key ONLY once the call
  // SUCCEEDS, so a FAILED generate never leaves `builtForKey` stuck at the failed
  // selection (which would make the auto-regenerate effect treat it as already built
  // and never retry). Re-entry during the in-flight call is prevented by the
  // 'generating' guard in the auto-regenerate effect, not by an optimistic key.
  const regenerate = useCallback(
    async (key?: string): Promise<void> => {
      // A regenerate rebuilds from sources and DISCARDS manual block edits: take
      // ownership so any pending/in-flight PATCH is cancelled or ignored, and clear
      // the edit error so a stale save failure does not linger over the fresh issue.
      takeIssueOwnership();
      setEditError(null);
      // C1 suspenders: the epoch takeIssueOwnership just bumped, and the newsletter
      // this generate is for, so a result that resolves after a newsletter switch
      // racing the unmount cannot settle onto (or stop the poll of) another newsletter.
      const nid = newsletterId;
      const epochAtSend = issueEpoch.current;
      setIssueCache((prev) => ({
        ...prev,
        state: 'generating',
        error: null,
      }));
      try {
        // Generation is async: this returns a `generating` issue (the studio then
        // polls) unless the backend could satisfy it with a finished issue directly.
        // `settleIssue` handles both, stamping the built-for-key only once finished.
        const fresh = await generateIssue(deps, nid);
        if (newsletterId !== nid || issueEpoch.current !== epochAtSend) return;
        settleIssue(fresh, key ?? null);
      } catch (caught) {
        if (newsletterId !== nid || issueEpoch.current !== epochAtSend) return;
        setIssueCache((prev) => ({
          ...prev,
          state: 'error',
          error: caught instanceof Error ? caught.message : 'Could not build your issue.',
          loaded: true,
        }));
      }
    },
    [deps, newsletterId, setIssueCache, settleIssue, takeIssueOwnership],
  );

  // The INITIAL draft load (GET /issue) is done ONCE by the shared workspace shell (so a
  // direct Audience deep-link also has the draft for the Send panel; see workspace.tsx),
  // not here, which keeps the paid load to a single fetch per session and avoids a
  // Studio+workspace double-fetch. The Studio reads the result from the hoisted
  // issueCache and drives everything after it (regenerate, poll, per-section regen).

  // Poll GET /issue while the backend reports `generating`, so the studio shows the
  // finished issue the moment the off-request generator persists it. The effect only
  // runs in the `generating` state, so its cleanup stops the interval both on unmount
  // AND the instant the status leaves `generating`; the in-flight latch keeps a slow
  // GET from stacking ticks. A poll failure surfaces the error and stops (the state
  // leaves `generating`), so a persistently failing generation never polls forever.
  useEffect(() => {
    if (issueState !== 'generating') {
      return;
    }
    let active = true;
    const tick = (): void => {
      if (pollInFlight.current || !active) {
        return;
      }
      pollInFlight.current = true;
      getIssue(deps, newsletterId)
        .then((fresh) => {
          if (active) settleIssue(fresh, pendingBuiltKey.current);
        })
        .catch((caught: unknown) => {
          if (!active) return;
          setIssueCache((prev) => ({
            ...prev,
            state: 'error',
            error: caught instanceof Error ? caught.message : 'Could not build your issue.',
            loaded: true,
          }));
        })
        .finally(() => {
          pollInFlight.current = false;
        });
    };
    const timer = setInterval(tick, ISSUE_POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [issueState, deps, newsletterId, settleIssue, setIssueCache]);

  // Auto-REGENERATE (debounced) whenever the selection signature changes after the
  // initial load, so the right pane tracks the curation. The built-for-key lives in
  // the hoisted cache, so a return to Design with an unchanged selection is a no-op
  // here and never re-fires the paid generate. Gated on a successful baseline
  // (builtForKey set) so a failed load does not silently auto-bill.
  //
  // The 'generating' guard is load-bearing for BILLING: a selection change WHILE a
  // generate is in flight must not fire a SECOND concurrent (billed) generate and a
  // last-write-wins race. It stays serial instead: when the in-flight generate
  // resolves, `issueState` flips to 'ready' and this effect re-runs, scheduling the
  // newer selection exactly once (its key differs from the just-built builtForKey).
  useEffect(() => {
    if (
      !loaded ||
      builtForKey === null ||
      issueState === 'loading' ||
      issueState === 'generating'
    ) {
      return;
    }
    if (!hasSelected || selectionKey.length === 0) {
      return;
    }
    // The selection matches what the current draft was built from: the draft is up to
    // date, so clear any stale "sources changed" prompt. This also fires when the
    // owner CHANGES the selection (warning shows) then REVERTS to the built one: the
    // early-return below would otherwise leave the warning stranded (bug fix).
    if (builtForKey === selectionKey) {
      setSourcesChanged(false);
      return;
    }
    // The selection differs from what the draft was built from. If the owner has manual
    // block edits, rebuilding from the changed sources would silently throw them away, so
    // surface a non-destructive "sources changed" prompt and let them rebuild explicitly
    // (#4). This is evaluated BEFORE the scheduledKey early-return on purpose (C4): when a
    // selection change armed the debounce while the draft was clean and `dirty` then flips
    // mid-window, the effect re-runs with scheduledKey already equal to selectionKey; if the
    // early-return ran first, the queued regenerate (cleared by this run's cleanup) would be
    // dropped AND the prompt never shown, leaving the right pane silently stale. Handling the
    // dirty case first guarantees a dirtied selection change always surfaces the prompt.
    if (dirty) {
      setSourcesChanged(true);
      return;
    }
    if (scheduledKey.current === selectionKey) {
      return;
    }
    scheduledKey.current = selectionKey;
    // A clean draft keeps the seamless live mirror: auto-regenerate after a short debounce.
    const timer = setTimeout(() => {
      void regenerate(selectionKey);
    }, ISSUE_REGEN_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [loaded, builtForKey, issueState, hasSelected, selectionKey, regenerate, dirty]);

  // The "just updated" wash on a regenerated block is a brief confirmation, so clear it
  // after a short beat (leaving the fresh prose in place).
  useEffect(() => {
    if (justUpdatedBlockId === null) {
      return;
    }
    const timer = setTimeout(() => setJustUpdatedBlockId(null), 2500);
    return () => clearTimeout(timer);
  }, [justUpdatedBlockId]);

  // Flush any pending debounced PATCH on unmount so a just-typed edit is not lost
  // when the studio unmounts (a Design/Operations tab switch mid-edit).
  useEffect(() => {
    return () => {
      if (patchTimer.current !== null) {
        clearTimeout(patchTimer.current);
        patchTimer.current = null;
        if (patchDirty.current) {
          void flushRef.current();
        }
      }
      // A just-typed instruction must not be lost on a tab switch mid-edit either.
      if (instructionTimer.current !== null) {
        clearTimeout(instructionTimer.current);
        instructionTimer.current = null;
        void flushInstructionsRef.current();
      }
      // Nor a just-picked treatment.
      if (treatmentTimer.current !== null) {
        clearTimeout(treatmentTimer.current);
        treatmentTimer.current = null;
        void flushTreatmentsRef.current();
      }
    };
  }, []);

  const busy = issueState === 'loading' || issueState === 'generating';
  const railBlocks = issue !== null ? railBlocksInOrder(issue) : [];
  const keptCount = railBlocks.filter((block) => block.status === 'kept').length;
  // The right-pane heading is always forward-looking: the preview is the NEXT issue to
  // go out (it will not always be the first), so it reads "Your next issue" in all cases.
  const issueHeading = 'Your next issue';

  // ---- Pipeline derivations (map + inspector) ----
  // The distinct colour-coded sources feeding the newsletter, the section nodes (the
  // issue's blocks in reading order, enriched with the discovered section behind each),
  // and the colour lookup shared by the map's binding dots and the inspector's chips.
  // Memoised on [sections, issue] so a block-edit keystroke (which re-renders here to
  // mirror the optimistic edit) never re-derives all three on every character.
  const pipelineSources = useMemo(() => derivePipelineSources(sections), [sections]);
  const colorByKey = useMemo(() => sourceColorMap(pipelineSources), [pipelineSources]);
  const sectionNodes = useMemo(
    () => derivePipelineSections(issue, sections, colorByKey),
    [issue, sections, colorByKey],
  );

  // The lead section to open the inspector on: the first kept STORY block, else the
  // first kept section node, so the inspector opens on real content (Treatment + bound
  // sources + validation + prose editor) instead of the inert Issue shape.
  const leadSectionId =
    sectionNodes.find((node) => node.status !== 'dismissed' && node.role === 'story')?.blockId ??
    sectionNodes.find((node) => node.status !== 'dismissed')?.blockId ??
    null;

  // Auto-select the lead section once the issue is ready and nothing is selected (a
  // fresh load, or after a regenerate cleared the stale selection), so the inspector
  // is never sitting on the sender-config seam when there is a section to edit.
  useEffect(() => {
    if (!busy && issue !== null && selectedNode === null && leadSectionId !== null) {
      setSelectedNode({ kind: 'section', id: leadSectionId });
      setActiveBlockId(leadSectionId);
    }
  }, [busy, issue, selectedNode, leadSectionId]);

  // The persistent, navigable roll-up of still-undecided seed drafts: each undecided
  // draft resolved to its section node so the owner can JUMP to it (by pipeline node id)
  // and keep/dismiss it, no matter which section is currently open. Retires itself as the
  // map empties (kept/dismissed drafts leave `seedDrafts`), so the count never goes stale.
  const seedPending: SeedRollupEntry[] = useMemo(() => {
    const bySectionId = new Map(
      sectionNodes.flatMap((node) =>
        node.section !== null ? [[node.section.sectionId, node] as const] : [],
      ),
    );
    return [...seedDrafts.keys()].flatMap((sectionId) => {
      const node = bySectionId.get(sectionId);
      return node !== undefined ? [{ sectionId, title: node.title, nodeId: node.blockId }] : [];
    });
  }, [seedDrafts, sectionNodes]);

  const seedUx: SeedUx = useMemo(
    () => ({
      state: seedState,
      error: seedError,
      likedCount: newsletter.likedNewsletters?.length ?? 0,
      ranEmpty: seedRanEmpty,
      missed: seedMissed,
      pending: seedPending,
      keptSectionId: seedKeptSectionId,
      seedFor: (sectionId: string) => seedDrafts.get(sectionId) ?? null,
      onSeed: () => void onSeedInstructions(),
      onKeep: onKeepSeed,
      onDismiss: onDismissSeed,
      onNavigate: (nodeId: string) => onSelectNode({ kind: 'section', id: nodeId }),
      onGoToAbout,
    }),
    [
      seedState,
      seedError,
      newsletter.likedNewsletters,
      seedRanEmpty,
      seedMissed,
      seedPending,
      seedKeptSectionId,
      seedDrafts,
      onSeedInstructions,
      onKeepSeed,
      onDismissSeed,
      onSelectNode,
      onGoToAbout,
    ],
  );

  // The inspector is IDENTICAL across Write and Wire modes (same node, same editing
  // controls), so it is built once and rendered in whichever mode is active.
  const inspectorEl = (
    <Inspector
      selected={selectedNode}
      sectionNodes={sectionNodes}
      sources={pipelineSources}
      issue={issue}
      newsletter={newsletter}
      railBlocks={railBlocks}
      keptCount={keptCount}
      editError={editError}
      busy={busy}
      focusTarget={focusTarget}
      onFocusHandled={clearFocusTarget}
      highlightId={activeBlockId}
      onHoverChange={onRailHoverChange}
      onToggleKept={onToggleKept}
      onMove={onMoveBlock}
      onEditTitle={onEditTitle}
      onEditBody={onEditBody}
      onEditInstruction={onEditInstruction}
      onEditTreatment={onEditTreatment}
      onRegenerateSection={onRegenerateSection}
      regeneratingBlockId={regeneratingBlockId}
      regenStatus={regenStatus}
      seed={seedUx}
      onSelect={onSelectNode}
    />
  );

  return (
    <div className="nlv__studio-shell">
      {/* The EDITOR'S OWN TOOLBAR: the Write | Wire mode switch. Deliberately styled as a
          left-aligned toolbar strip (surface-muted bar, "Editor" label) so it reads as the
          editor's controls, NOT as a second tab strip stacked under the Studio/Audience/
          Basics section nav above it. Write is the compose surface; Wire is the alternative
          drawn-edge pipeline graph. Both keep the same inspector + selection. */}
      <div className="nlv__studio-toolbar" role="toolbar" aria-label="Editor tools">
        <span className="nlv__studio-toolbar-label" id="nlv-studio-mode">
          Editor
        </span>
        <SegmentedControl
          options={MODE_OPTIONS}
          value={mode}
          onChange={setMode}
          aria-label="Editor view mode"
        />
      </div>

      {mode === 'wire' ? (
        <div className="nlv__wire-studio">
          <p className="nlv__wire-desktop-note" role="note">
            Wire mode is a desktop view, it is best on a larger screen.
          </p>
          <div className="nlv__wire-graph-col">
            <WireGraph
              sources={pipelineSources}
              sectionNodes={sectionNodes}
              issueStatus={issue?.status ?? null}
              selected={selectedNode}
              highlightId={activeBlockId}
              busy={busy}
              onSelect={onSelectNode}
            />
          </div>
          <div className="nlv__studio-inspector-col">{inspectorEl}</div>
        </div>
      ) : mode === 'preview' ? (
        /* PREVIEW: the issue on its own, full-width and centred at a comfortable reading
           measure, exactly as a reader will see it. No map/inspector, and the IssueRenderer
           is handed NO hover/select handlers or highlight id, so there is no editing chrome,
           just a clean read. Handles the same busy/generating/empty/error states the Write
           centre pane does, reading the same hoisted issue cache. */
        <div className="nlv__preview">
          <section className="nlv__panel nlv__issue-panel" aria-labelledby="nlv-preview">
            <div className="nlv__issue-panel-head">
              <h2 id="nlv-preview" className="nlv__panel-title">
                {issueHeading}
              </h2>
            </div>
            <p className="nlv__panel-lead">What your readers will see.</p>

            {busy && (
              <p className="dash__status nlv__block-rail-busy" role="status">
                Updating…
              </p>
            )}

            {!canAssemble && (
              <p className="dash__hint">
                {sectionsLoading
                  ? 'Finding your first sources…'
                  : 'As you keep sources on the left, we draft your issue here.'}
              </p>
            )}

            {busy && issue === null && (
              <div className="nlv__issue-skeleton">
                <p className="dash__status nlv__issue-skeleton-caption" role="status">
                  Drafting your next issue…
                </p>
                <Skeleton lines={6} aria-label="Drafting your next issue" />
              </div>
            )}

            {issueState === 'error' && issueError !== null && (
              <Notification variant="error">{issueError}</Notification>
            )}

            {issue !== null && (
              <div
                className={`nlv__issue-live${busy ? ' nlv__issue-live--busy' : ''}`}
                aria-busy={busy ? true : undefined}
              >
                <IssueRenderer issue={issue} newsletter={newsletter} />
              </div>
            )}
          </section>
        </div>
      ) : (
        /* WRITE: a clean TWO-region layout - the pipeline MAP (the primary structural
           canvas, left rail) and the INSPECTOR (the editing panel, main area). The
           rendered issue lives ONLY in Preview now, so there is no central reader pane
           here. A slim, full-width WRITE BAR above the two regions keeps the WHOLE-ISSUE
           actions reachable: Regenerate, the sources-changed prompt, and the on-demand
           Refresh opening. Per-section editing (instruction / treatment / prose / regen /
           seed) all lives in the inspector, driven by the map selection. */
        <div className="nlv__studio" data-pane={mobilePane}>
          <div className="nlv__studio-toggle">
            <span className="nlv__studio-toggle-label" id="nlv-studio-view">
              Show
            </span>
            <SegmentedControl
              options={PANE_OPTIONS}
              value={mobilePane}
              onChange={setMobilePane}
              aria-label="Show which studio pane"
            />
          </div>

          {/* WRITE BAR: the whole-issue status + actions. The rendered issue itself is in
              Preview; this keeps its issue-level controls reachable while Write edits. */}
          <div className="nlv__write-bar">
            <div className="nlv__issue-panel-head">
              <h2 id="nlv-issue" className="nlv__panel-title">
                {issueHeading}
              </h2>
              <Button
                variant="ghost"
                title={REGEN_HINT}
                onClick={() => void regenerate(selectionKey)}
                disabled={busy || !canAssemble || regeneratingBlockId !== null}
              >
                {issueState === 'generating' ? 'Regenerating…' : 'Regenerate'}
              </Button>
            </div>
            <p className="nlv__panel-lead">
              Build your issue here with the map and inspector. Open Preview to read it as your
              readers will.
            </p>

            {/* When a load/regenerate owns the issue, editing is paused; say why. */}
            {busy && (
              <p className="dash__status nlv__block-rail-busy" role="status">
                Updating…
              </p>
            )}

            {/* A source change over a dirty draft prompts before rebuilding, so manual
                block edits are never silently discarded. */}
            {sourcesChanged && (
              <Notification variant="warning">
                <span className="nlv__sources-changed">
                  Your sources changed. Regenerating rebuilds this issue from them and will replace
                  the edits you have made here.
                </span>
                <Button
                  variant="ghost"
                  className="nlv__sources-changed-btn"
                  title={REGEN_HINT}
                  disabled={busy || regeneratingBlockId !== null}
                  onClick={() => void regenerate(selectionKey)}
                >
                  {issueState === 'generating' ? 'Regenerating…' : 'Regenerate'}
                </Button>
              </Notification>
            )}

            {/* After a per-section regenerate, the subject + intro can no longer match the
                changed section, so offer an on-demand opening resync (never auto-run). */}
            {openingMaybeStale && issue !== null && (
              <Notification variant="info">
                <span className="nlv__opening-stale">
                  You changed a section, so the subject and intro may not match it.
                </span>
                <span className="nlv__opening-stale-actions">
                  <Button
                    variant="primary"
                    className="nlv__opening-stale-btn"
                    disabled={busy || regeneratingBlockId !== null}
                    loading={refreshingOpening}
                    onClick={() => void onRefreshOpening()}
                  >
                    {refreshingOpening ? 'Refreshing…' : 'Refresh opening'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="nlv__opening-stale-btn"
                    disabled={refreshingOpening}
                    onClick={() => setOpeningMaybeStale(false)}
                  >
                    Dismiss
                  </Button>
                </span>
              </Notification>
            )}

            {!canAssemble && (
              <p className="dash__hint">
                {sectionsLoading
                  ? 'Finding your first sources…'
                  : 'As you keep sources on the left, we draft your issue here.'}
              </p>
            )}

            {/* A first-load/first-generate wait, when there is no draft yet, reads as
                intentional rather than stalled. */}
            {busy && issue === null && (
              <p className="dash__status nlv__issue-skeleton-caption" role="status">
                Drafting your next issue…
              </p>
            )}

            {/* Whole-issue error surfaces here as a banner. */}
            {issueState === 'error' && issueError !== null && (
              <Notification variant="error">{issueError}</Notification>
            )}
          </div>

          {/* LEFT: the pipeline map (how this issue is built), with the section-curation
              controls and the refine chat docked beneath it. */}
          <div className="nlv__studio-map-col">
            <PipelineMap
              sources={pipelineSources}
              sectionNodes={sectionNodes}
              issueStatus={issue?.status ?? null}
              selected={selectedNode}
              highlightId={activeBlockId}
              onSelect={onSelectNode}
            />

            <details className="nlv__panel nlv__sources-disclosure" open>
              <summary className="nlv__panel-summary">
                <span className="nlv__panel-heading">Sources &amp; structure</span>
                <span className="nlv__panel-hint">Choose what your issue is built from</span>
              </summary>
              <div className="nlv__panel-body">
                <SectionsPanel
                  deps={deps}
                  newsletterId={newsletterId}
                  hasTopics={hasTopics}
                  sections={sections}
                  state={discoverState}
                  error={discoverError}
                  busy={sectionsLoading}
                  onRediscover={() => runDiscover(newsletter.topics)}
                  onSectionsChange={setSections}
                  setupTo="basics"
                />
              </div>
            </details>

            <RefineChat
              deps={deps}
              newsletterId={newsletterId}
              onNewsletterChange={setNewsletter}
              onSectionsChange={setSections}
              turns={refineTurns}
              onTurnsChange={setRefineTurns}
              input={refineInput}
              onInputChange={setRefineInput}
              nextId={refineNextId}
            />
          </div>

          {/* RIGHT (main): the inspector for the selected node, where all section editing
              lives (rename / rewrite / keep-dismiss / reorder via BlockCard, instruction,
              treatment, per-section regenerate, seed). */}
          <div className="nlv__studio-inspector-col">{inspectorEl}</div>
        </div>
      )}
    </div>
  );
}
